import React, { useState, useEffect } from "react";
import {
  getReviewCountsByRating,
  getReviewSources,
  getReviewCountsBySource,
  getReviewSource,
  getReviewRating,
  getReviewTimestamp
} from "../utils/reviewUtils";
import { useAuth } from "../../../contexts/AuthContext";
import { updateReview, deleteReview, fetchReviews } from "../../../services/organisationService";
import ReviewModal from "../../ReviewModal";
import DeleteConfirmationModal from "../../DeleteConfirmationModal";
import { toast } from "react-hot-toast";
import { FaStar } from 'react-icons/fa';
import axios from 'axios';

const ReviewsSection = ({
  reviews,
  filteredReviews,
  reviewFilter,
  sourceFilter,
  sortFilter,
  isSorting,
  currentUser,
  canUserReview,
  lawyer,
  onLeaveReview,
  onClaimListing,
  canClaimListing,
  reviewSectionRef,
  addReviewBtnRef,
  setReviewFilter,
  setSourceFilter,
  setSortFilter,
  setSortCounter,
  refreshReviews
}) => {
  const { user } = useAuth();
  const [reviewToEdit, setReviewToEdit] = useState(null);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Pagination state for Serp reviews
  const [currentPage, setCurrentPage] = useState(1);
  const [extraReviews, setExtraReviews] = useState([]);
  const [hasMore, setHasMore] = useState(reviews?.has_more ?? false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Keep hasMore in sync when top-level reviews prop updates (e.g., after refresh)
  useEffect(() => {
    if (typeof reviews?.has_more !== 'undefined') {
      setHasMore(reviews.has_more);
    } else if (reviews?.stats && reviews?.reviews) {
      setHasMore(reviews.stats.total_reviews > reviews.reviews.length);
    }
  }, [reviews]);

  // Check if a review belongs to the current user
  const isUserReview = (review) => {
    if (!user) return false;
    return review.source === 'zahoot' && user.email === review.email;
  };

  // Check if the current user can delete a review
  const canDeleteReview = (review) => {
    if (!user) return false;
    
    // User can delete their own review
    const isOwnReview = review.source === 'zahoot' && user.email === review.email;
    
    // Admin and manager can delete any review
    const isAdminOrManager = user.role === 'admin' || user.role === 'manager' || 
                            user.role_name === 'admin' || user.role_name === 'manager';
    
    return isOwnReview || isAdminOrManager;
  };

  // Edit review functionality
  const handleEditClick = (review) => {
    setReviewToEdit(review);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (updatedReview) => {
    try {
      setIsProcessing(true);
      await updateReview(reviewToEdit.id, updatedReview);
      
      // Close modal and refresh reviews
      setShowEditModal(false);
      setReviewToEdit(null);
      
      // Refresh reviews
      if (refreshReviews) {
        await refreshReviews();
      }
      
      toast.success('Review updated successfully!');
    } catch (error) {
      console.error('Error updating review:', error);
      toast.error(error.response?.data?.message || 'Failed to update review. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete review functionality
  const handleDeleteClick = (review) => {
    setReviewToDelete(review);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsProcessing(true);
      await deleteReview(reviewToDelete.id);
      
      // Close modal and refresh reviews
      setShowDeleteModal(false);
      setReviewToDelete(null);
      
      // Refresh reviews
      if (refreshReviews) {
        await refreshReviews();
      }
      
      toast.success('Review deleted successfully!');
    } catch (error) {
      console.error('Error deleting review:', error);
      toast.error(error.response?.data?.message || 'Failed to delete review. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Format date with ordinal suffix and time
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    
    // Format the date part (day with ordinal suffix)
    const day = date.getDate();
    const ordinalSuffix = getOrdinalSuffix(day);
    const month = date.toLocaleDateString('en-GB', { month: 'long' });
    const year = date.getFullYear();
    
    // Format the time part (with AM/PM)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';
    const formattedHours = hours % 12 || 12; // Convert to 12-hour format
    const formattedMinutes = minutes.toString().padStart(2, '0');
    
    return `${day}${ordinalSuffix} ${month} ${year} @ ${formattedHours}:${formattedMinutes}${ampm}`;
  };
  
  // Helper function to get ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Merge initial filteredReviews with any extra loaded pages (apply simple de-duplication by id+source)
  const combinedReviews = (() => {
    const map = new Map();
    [...filteredReviews, ...extraReviews].forEach(r => {
      const key = `${r.source}-${r.id || r.email || r.time}`;
      map.set(key, r);
    });
    return Array.from(map.values());
  })();

  // Get unique locations for filter dropdown (enhanced)
  const getUniqueLocations = () => {
    if (!reviews || !reviews.reviews) return [];
    
    const locations = [...new Set(
      reviews.reviews
        .map(review => review.location_name || getReviewSource(review) === 'google' ? review.location_name : null)
        .filter(Boolean)
    )];
    return locations.sort();
  };

  // Add location filter state
  const [locationFilter, setLocationFilter] = useState('all');
  
  // Enhanced filtering that includes location filter
  const getFilteredReviews = () => {
    let filtered = [...combinedReviews];
    
    // Apply rating filter
    if (reviewFilter !== 'all') {
      const ratingValue = parseInt(reviewFilter);
      filtered = filtered.filter(review => Math.round(getReviewRating(review)) === ratingValue);
    }
    
    // Apply source filter
    if (sourceFilter !== 'all') {
      filtered = filtered.filter(review => getReviewSource(review) === sourceFilter);
    }
    
    // Apply location filter
    if (locationFilter !== 'all') {
      if (locationFilter === 'no-location') {
        filtered = filtered.filter(review => !review.location_name);
      } else {
        filtered = filtered.filter(review => review.location_name === locationFilter);
      }
    }
    
    return filtered;
  };

  const displayReviews = getFilteredReviews();

  const handleLoadMore = async () => {
    if (!lawyer || !lawyer.id) return;
    setIsLoadingMore(true);
    try {
      const nextPage = currentPage + 1;
      const res = await fetchReviews(lawyer.id, nextPage);
      setExtraReviews(prev => [...prev, ...(res.reviews || [])]);
      setCurrentPage(nextPage);
      if (typeof res.has_more !== 'undefined') {
        setHasMore(res.has_more);
      } else if (res.stats && res.stats.total_reviews) {
        // fallback – if stats suggests more than we have after appending
        const totalAfterAppend = filteredReviews.length + extraReviews.length + (res.reviews || []).length;
        setHasMore(res.stats.total_reviews > totalAfterAppend);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more reviews', err);
      toast.error('Failed to load more reviews. Please try again.');
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Show professional skeleton loader when reviews are initially loading
  if (!reviews || (reviews && !reviews.reviews && !reviews.error)) {
    return (
      <div ref={reviewSectionRef}>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
          <svg
            className="w-5 h-5 mr-2 text-indigo-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            ></path>
          </svg>
          Client Reviews
        </h3>
        
        {/* Professional skeleton loader */}
        <div className="animate-pulse space-y-4 sm:space-y-6">
          {/* Rating distribution skeleton */}
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 sm:p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-28 sm:w-32"></div>
              <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16 sm:w-20"></div>
            </div>
            <div className="space-y-2 sm:space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center space-x-2 sm:space-x-3">
                  <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-6 sm:w-8"></div>
                  <div className="flex-1 h-2 sm:h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded"></div>
                  <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-10 sm:w-12"></div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Filter buttons skeleton */}
          <div className="space-y-2 sm:space-y-3">
            <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20 sm:w-24"></div>
            <div className="flex flex-wrap gap-1.5 sm:gap-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-7 sm:h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full w-16 sm:w-20"></div>
              ))}
            </div>
          </div>
          
          {/* Review cards skeleton */}
          <div className="space-y-3 sm:space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-white p-4 sm:p-6 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex-shrink-0"></div>
                  <div className="flex-1 space-y-2 sm:space-y-3 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-24 sm:w-32"></div>
                      <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16 sm:w-20"></div>
                    </div>
                    <div className="flex space-x-1">
                      {[...Array(5)].map((_, j) => (
                        <div key={j} className="w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-yellow-200 to-yellow-300 rounded"></div>
                      ))}
                    </div>
                    <div className="space-y-1.5 sm:space-y-2">
                      <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full"></div>
                      <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-4/5"></div>
                      <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/5 sm:w-2/3"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={reviewSectionRef}>
      <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
        <svg
          className="w-5 h-5 mr-2 text-indigo-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
          ></path>
        </svg>
        Client Reviews
      </h3>

      {/* Review filters */}
      {reviews && reviews.reviews && reviews.reviews.length > 0 && (
        <>
          {/* Rating distribution bar */}
          <div className="mb-6 bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-medium text-gray-700">Rating Distribution</h4>
              <span className="text-sm text-gray-500">{reviews.reviews.length} total reviews</span>
            </div>
            
            {/* Calculate counts by rating */}
            {(() => {
              const ratingCounts = getReviewCountsByRating(reviews.reviews);
              return [5, 4, 3, 2, 1].map(rating => {
                const count = ratingCounts[rating] || 0;
                const percentage = reviews.reviews.length > 0 
                  ? Math.round((count / reviews.reviews.length) * 100) 
                  : 0;
                return (
                  <div key={rating} className="mb-2">
                    <div className="flex items-center">
                      <span className="text-sm text-gray-700 w-10">{rating} ★</span>
                      <div className="flex-1 h-4 mx-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-yellow-400 rounded-full" 
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-500 w-14">{count} ({percentage}%)</span>
                    </div>
                  </div>
                );
              });
            })()}
          </div>

          {/* Filter Section */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-gray-700">Filter by rating</h4>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setReviewFilter('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-full flex items-center ${
                  reviewFilter === 'all'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } transition-colors`}
              >
                <span>All Ratings</span>
                <span className={`ml-2 ${reviewFilter === 'all' ? 'bg-white text-indigo-600' : 'bg-gray-200 text-gray-700'} px-1.5 py-0.5 text-xs rounded-full`}>
                  {reviews.reviews.length}
                </span>
              </button>
              
              {[5, 4, 3, 2, 1].map(rating => {
                const count = (getReviewCountsByRating(reviews.reviews)[rating] || 0);
                if (count === 0) return null;
                
                return (
                  <button
                    key={rating}
                    onClick={() => setReviewFilter(rating.toString())}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full flex items-center ${
                      reviewFilter === rating.toString()
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <span>{rating} Stars</span>
                    <span className={`ml-2 ${reviewFilter === rating.toString() ? 'bg-white text-indigo-600' : 'bg-gray-200 text-gray-700'} px-1.5 py-0.5 text-xs rounded-full`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
            
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-gray-700">Filter by source</h4>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => setSourceFilter('all')}
                className={`px-3 py-1.5 text-sm font-medium rounded-full flex items-center ${
                  sourceFilter === 'all'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } transition-colors`}
              >
                <span>All Sources</span>
                <span className={`ml-2 ${sourceFilter === 'all' ? 'bg-white text-indigo-600' : 'bg-gray-200 text-gray-700'} px-1.5 py-0.5 text-xs rounded-full`}>
                  {reviews.reviews.length}
                </span>
              </button>
              
              {Object.entries(getReviewCountsBySource(reviews.reviews)).map(([source, count]) => {
                if (count === 0) return null;
                const sourceName = source === 'zahoot' ? 'Zahoot' : source === 'google' ? 'Google' : source;
                
                return (
                  <button
                    key={source}
                    onClick={() => setSourceFilter(source)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full flex items-center ${
                      sourceFilter === source
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <span>{sourceName}</span>
                    <span className={`ml-2 ${sourceFilter === source ? 'bg-white text-indigo-600' : 'bg-gray-200 text-gray-700'} px-1.5 py-0.5 text-xs rounded-full`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
            
            {/* Location filter - only show if there are multiple locations */}
            {getUniqueLocations().length > 0 && (
              <>
                <div className="flex justify-between items-center mb-2">
                  <h4 className="text-sm font-medium text-gray-700">Filter by location</h4>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  <button
                    onClick={() => setLocationFilter('all')}
                    className={`px-3 py-1.5 text-sm font-medium rounded-full flex items-center ${
                      locationFilter === 'all'
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <span>All Locations</span>
                    <span className={`ml-2 ${locationFilter === 'all' ? 'bg-white text-indigo-600' : 'bg-gray-200 text-gray-700'} px-1.5 py-0.5 text-xs rounded-full`}>
                      {combinedReviews.length}
                    </span>
                  </button>
                  
                  {getUniqueLocations().map(location => {
                    const count = combinedReviews.filter(r => r.location_name === location).length;
                    return (
                      <button
                        key={location}
                        onClick={() => setLocationFilter(location)}
                        className={`px-3 py-1.5 text-sm font-medium rounded-full flex items-center ${
                          locationFilter === location
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        } transition-colors`}
                      >
                        <span>{location}</span>
                        <span className={`ml-2 ${locationFilter === location ? 'bg-white text-indigo-600' : 'bg-gray-200 text-gray-700'} px-1.5 py-0.5 text-xs rounded-full`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                  
                  {/* No location filter */}
                  {combinedReviews.some(r => !r.location_name) && (
                    <button
                      onClick={() => setLocationFilter('no-location')}
                      className={`px-3 py-1.5 text-sm font-medium rounded-full flex items-center ${
                        locationFilter === 'no-location'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      } transition-colors`}
                    >
                      <span>No Location</span>
                      <span className={`ml-2 ${locationFilter === 'no-location' ? 'bg-white text-indigo-600' : 'bg-gray-200 text-gray-700'} px-1.5 py-0.5 text-xs rounded-full`}>
                        {combinedReviews.filter(r => !r.location_name).length}
                      </span>
                    </button>
                  )}
                </div>
              </>
            )}
            
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-gray-700">Sort by</h4>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              <button
                onClick={() => {
                  setSortFilter('newest');
                  setSortCounter(prev => prev + 1);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                  sortFilter === 'newest'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } transition-colors`}
              >
                Newest First
              </button>
              <button
                onClick={() => {
                  setSortFilter('oldest');
                  setSortCounter(prev => prev + 1);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                  sortFilter === 'oldest'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } transition-colors`}
              >
                Oldest First
              </button>
              <button
                onClick={() => {
                  setSortFilter('highest');
                  setSortCounter(prev => prev + 1);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                  sortFilter === 'highest'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } transition-colors`}
              >
                Highest Rated
              </button>
              <button
                onClick={() => {
                  setSortFilter('lowest');
                  setSortCounter(prev => prev + 1);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-full ${
                  sortFilter === 'lowest'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } transition-colors`}
              >
                Lowest Rated
              </button>
            </div>
          </div>
        </>
      )}

      {reviews && reviews.reviews && reviews.reviews.length > 0 ? (
        <div className="space-y-4">
          {displayReviews.length > 0 ? (
            <>
              {displayReviews.map((review, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex">
                      <div className="flex mr-2">
                        {[...Array(5)].map((_, i) => (
                          <svg
                            key={i}
                            className={`w-4 h-4 ${i < getReviewRating(review) ? "text-yellow-400" : "text-gray-300"}`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                          </svg>
                        ))}
                      </div>
                    </div>
                    
                    {/* Show review source */}
                    <div className="flex items-center">
                      {getReviewSource(review) === 'google' && (
                        <svg className="w-4 h-4 text-blue-500 mr-1" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12.545,10.239v3.821h5.445c-0.712,2.315-2.647,3.972-5.445,3.972c-3.332,0-6.033-2.701-6.033-6.032s2.701-6.032,6.033-6.032c1.498,0,2.866,0.549,3.921,1.453l2.814-2.814C17.503,2.988,15.139,2,12.545,2C7.021,2,2.543,6.477,2.543,12s4.478,10,10.002,10c8.396,0,10.249-7.85,9.426-11.748L12.545,10.239z"/>
                        </svg>
                      )}
                      {getReviewSource(review) === 'zahoot' && (
                        <svg className="w-4 h-4 text-green-500 mr-1" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                      )}
                      <span className="text-xs text-gray-500">
                        {getReviewSource(review) === 'google' ? 'Google Review' : 'Verified Review'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mb-2">
                    <p className="font-medium text-gray-800">{review.name || review.author_name || "Anonymous"}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <span>
                        {getReviewSource(review) === 'google' && review.relative_time_description 
                          ? review.relative_time_description
                          : formatDate(review.time || review.created_at || Date.now())}
                      </span>
                      
                      {/* Location badge for reviews with location information */}
                      {review.location_name && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                          📍 {review.location_name}
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">{review.comment || review.text}</p>
                  
                  {/* Edit/Delete buttons for user's own reviews */}
                  {isUserReview(review) && (
                    <div className="mt-3 flex space-x-2 justify-end">
                      <button 
                        onClick={() => handleEditClick(review)}
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                        disabled={isProcessing}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 0L11.828 15H9v-2.828l8.586-8.586z"></path>
                        </svg>
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDeleteClick(review)}
                        className="text-sm text-red-600 hover:text-red-800 flex items-center"
                        disabled={isProcessing}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                        Delete
                      </button>
                    </div>
                  )}
                  
                  {/* Delete button for admin/manager for reviews that aren't their own */}
                  {!isUserReview(review) && canDeleteReview(review) && (
                    <div className="mt-3 flex space-x-2 justify-end">
                      <button 
                        onClick={() => handleDeleteClick(review)}
                        className="text-sm text-red-600 hover:text-red-800 flex items-center"
                        disabled={isProcessing}
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                        </svg>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <svg
                className="w-12 h-12 mx-auto text-gray-400 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                ></path>
              </svg>
              <p className="text-lg text-gray-600 font-medium">No reviews match your filters</p>
              <p className="text-gray-500 mt-2">Try adjusting your filters to see more reviews</p>
              <button
                onClick={() => {
                  setReviewFilter('all');
                  setSourceFilter('all');
                  setLocationFilter('all');
                }}
                className="mt-4 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-sm font-medium rounded-lg transition-colors"
              >
                Clear all filters
              </button>
            </div>
          )}

          {/* Add review button for authenticated users */}
          {canUserReview ? (
            <button
              ref={addReviewBtnRef}
              onClick={onLeaveReview}
              className="mt-4 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-sm font-medium rounded-lg transition-colors flex items-center"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                ></path>
              </svg>
              Add your review
            </button>
          ) : !currentUser ? (
            <p className="mt-4 text-gray-600 text-sm">
              Please <a href="/signin" className="text-indigo-600 hover:text-indigo-800">log in</a> to leave a review
            </p>
          ) : currentUser.role === 'legal_professional' || currentUser.role_name === 'legal_professional' ? (
            <p className="mt-4 text-gray-600 text-sm">
              Legal professionals cannot leave reviews
            </p>
          ) : (
            <p className="mt-4 text-gray-600 text-sm">
              You have already reviewed this lawyer
            </p>
          )}

          {/* Add Claim Listing button for unclaimed profiles */}
          {currentUser && 
           (currentUser.role === 'legal_professional' || currentUser.role_name === 'legal_professional') && 
           canClaimListing && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">
                Is this your profile?
              </h4>
              <p className="text-sm text-blue-600 mb-3">
                If you're a legal professional at {lawyer.practice_name}, you can claim this listing to update information, respond to reviews, and more.
              </p>
              <button
                onClick={onClaimListing}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center"
              >
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
                Claim this listing
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <svg
            className="w-12 h-12 mx-auto text-gray-400 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
            ></path>
          </svg>
          <p className="text-lg text-gray-600 font-medium">No reviews yet</p>
          <p className="text-gray-500">Be the first to share your experience with this lawyer</p>
          
          {canUserReview ? (
            <button
              ref={addReviewBtnRef}
              onClick={onLeaveReview}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center mx-auto"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
                ></path>
              </svg>
              Be the first to leave a review
            </button>
          ) : !currentUser ? (
            <p className="text-gray-600 text-sm">
              Please <a href="/login" className="text-indigo-600 hover:text-indigo-800">log in</a> to leave a review
            </p>
          ) : currentUser.role === 'legal_professional' || currentUser.role_name === 'legal_professional' ? (
            <p className="text-gray-600 text-sm">
              Legal professionals cannot leave reviews
            </p>
          ) : (
            <p className="text-gray-600 text-sm">
              You have already reviewed this lawyer
            </p>
          )}
        </div>
      )}
      
      {/* Edit Review Modal */}
      <ReviewModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setReviewToEdit(null);
        }}
        onSubmit={handleEditSubmit}
        lawyerName={lawyer?.practice_name || 'this lawyer'}
        isEdit={true}
        initialReview={reviewToEdit}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setReviewToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemType="Review"
      />

      {/* Load More Button for pagination */}
      {hasMore && (
        <div className="text-center mt-6">
          <button
            disabled={isLoadingMore}
            onClick={handleLoadMore}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto min-w-[160px] font-medium shadow-md hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] disabled:hover:scale-100 disabled:hover:shadow-md"
          >
            {isLoadingMore ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="font-medium">Loading reviews...</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Load More Reviews
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
};

export default ReviewsSection; 